import { Component, computed, inject, input, output } from '@angular/core';
import { FormControl, FormGroup, ReactiveFormsModule } from '@angular/forms';
import { provideIcons } from '@ng-icons/core';
import { lucideEye, lucideTrash } from '@ng-icons/lucide';
import { CheckboxSubmenuItemComponent } from '@shared/components/checkbox-submenu-item/checkbox-submenu-item.component';
import { ExpansionPanel, ExpansionPanelMenuAction } from '@shared/components/expansion-panel/expansion-panel.component';
import { LabelRequiredDirective } from '@shared/directives/label-required.directive';
import { HlmAutocompleteImports } from '@spartan-ng/helm/autocomplete';
import { HlmButtonImports } from '@spartan-ng/helm/button';
import { HlmFieldImports } from '@spartan-ng/helm/field';
import { HlmIconImports } from '@spartan-ng/helm/icon';
import { HlmInputImports } from '@spartan-ng/helm/input';
import { HlmItemSeparator } from '@spartan-ng/helm/item';
import { HlmRadioGroupImports } from '@spartan-ng/helm/radio-group';
import { FormControlNames } from '../../../../référentiel-métier-parcours/models/form-control-names.model';
import { Module } from '../../../../référentiel-métier-parcours/models/module.model';
import { ModuleDetailFormService } from '../../../../référentiel-métier-parcours/parcours-detail/module-detail-contenu/module-detail-form.service';
import { InterrelationStore } from '../../../../référentiel-métier/référentiel-metier-interrelations/store/interrelation.store';
import { Aptitude } from '../../../../référentiel-métier/référentiel-métier-processus/processus-aptitude/models/aptitude.model';
import { Production } from '../../../../référentiel-métier/référentiel-métier-processus/processus-aptitude/models/production.model';

@Component({
  selector: 'app-sequence-objectif-group',
  imports: [
    HlmInputImports,
    HlmFieldImports,
    ReactiveFormsModule,
    ExpansionPanel,
    CheckboxSubmenuItemComponent,
    HlmAutocompleteImports,
    HlmIconImports,
    HlmButtonImports,
    HlmItemSeparator,
    HlmRadioGroupImports,
    LabelRequiredDirective,
  ],
  templateUrl: './objectif-group.component.html',
  providers: [
    provideIcons({
      lucideTrash,
      lucideEye,
    }),
  ],
})
export class SéquenceObjectifGroupComponent {
  readonly #interrelationStore = inject(InterrelationStore);
  readonly form = input.required<FormGroup>();
  readonly index = input.required<number | string>();
  readonly supprimerAptitude = output();
  readonly module = input.required<Module>();
  readonly detailFormService = inject(ModuleDetailFormService);
  readonly aptitudesCertification = computed(() => this.detailFormService.aptitudesCertif());

  readonly actionAptitude: ExpansionPanelMenuAction<Aptitude>[] = [
    {
      icon: 'lucideTrash',
      label: "Supprimer l'aptitude du module",
      action: () => this.supprimerAptitude.emit(),
      class: 'invisible group-hover/item:visible',
    },
  ];

  redirectToRessourcesPro() {
    const url = location.href;
    const fragments = url.split('/').slice(0, 7);
    fragments.push('ressources-professionnelles');
    window.open(fragments.join('/'), '_blank');
  }

  getInterrelationsFromAptitude(aptitude: Aptitude) {
    const module = this.module();
    let interrelations: { id: string; nom: string }[] = [];
    if (module) {
      interrelations = this.#interrelationStore
        .entities()
        .filter(
          (x) =>
            x.idsPhase?.includes(aptitude.idPhase) &&
            module.interrelations.some((y) => y.idInterrelation === x.id && y.idAptitude === aptitude.id),
        )
        .map((x) => ({ id: `${aptitude.id}-${x.id}`, nom: x.nom }));
    }
    return interrelations;
  }

  getProductionsFromAptitude(aptitude: Aptitude) {
    const module = this.module();
    return module.productions.filter((x) => x.idAptitude === aptitude.id);
  }

  get interrelations() {
    return this.form().get(FormControlNames.INTERRELATIONS) as FormControl<{ id: string }[]>;
  }

  get productions() {
    return this.form().get(FormControlNames.PRODUCTIONS) as FormControl<Production[]>;
  }

  get aptitudeForm() {
    return this.form().get(FormControlNames.APTITUDE) as FormControl<Aptitude>;
  }
  get estEvaluee() {
    return this.form().get(FormControlNames.EST_EVALUEE) as FormControl<boolean>;
  }
}
